/* eslint-disable react/no-multi-comp */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { useIsPortalMobile } from '../useIsPortalMobile';
import { CALENDAR_WEEKDAYS, SCHEDULE_DEFAULT_DURATION_MINUTES, SCHEDULE_DURATION_MINUTES, SCHEDULE_MUTE, addCalendarMonths, addMinutesIso, buildCalendarCells, buildDefaultScheduleTitle, buildTimeOptions, formatCalendarTitle, formatDateWithWeekday, formatDurationLabel, getDefaultScheduleStart, parseDateParts, toScheduledIso, toStartAudioMuted } from '../scheduleUtils';
import ScheduleMeetingMobile from './ScheduleMeetingMobile';
/**
 * 预定会议弹窗：主题、开始（含星期）、时长、入会静音、自动云录制。
 *
 * @param {IProps} props - Props.
 * @returns {React.ReactElement}
 */
export default function ScheduleMeetingModal({ busy, displayName, error, onClose, onSubmit }) {
    const isMobile = useIsPortalMobile();
    if (isMobile) {
        return (React.createElement(ScheduleMeetingMobile, { busy: busy, displayName: displayName, error: error, onClose: onClose, onSubmit: onSubmit }));
    }
    return (React.createElement(ScheduleMeetingDesktop, { busy: busy, displayName: displayName, error: error, onClose: onClose, onSubmit: onSubmit }));
}
/**
 * 电脑 Web 预定会议：保持窗口模式。
 *
 * @param {IProps} props - Props.
 * @returns {React.ReactElement}
 */
function ScheduleMeetingDesktop({ busy, displayName, error, onClose, onSubmit }) {
    const defaultStart = useMemo(() => getDefaultScheduleStart(), []);
    const timeOptions = useMemo(() => buildTimeOptions(), []);
    const [title, setTitle] = useState(() => buildDefaultScheduleTitle(displayName));
    const [dateValue, setDateValue] = useState(defaultStart.date);
    const [timeValue, setTimeValue] = useState(defaultStart.time);
    const [durationMinutes, setDurationMinutes] = useState(SCHEDULE_DEFAULT_DURATION_MINUTES);
    const [muteMode, setMuteMode] = useState(SCHEDULE_MUTE.OFF);
    const [autoCloudRecording, setAutoCloudRecording] = useState(false);
    const [localError, setLocalError] = useState('');
    const [calendarOpen, setCalendarOpen] = useState(false);
    const selectedParts = parseDateParts(dateValue);
    const [calendarYear, setCalendarYear] = useState(selectedParts.year);
    const [calendarMonth, setCalendarMonth] = useState(selectedParts.month);
    const dateWrapRef = useRef(null);
    const calendarCells = useMemo(() => buildCalendarCells(calendarYear, calendarMonth), [calendarMonth, calendarYear]);
    useEffect(() => {
        if (!calendarOpen) {
            return undefined;
        }
        const onPointerDown = (event) => {
            if (dateWrapRef.current && !dateWrapRef.current.contains(event.target)) {
                setCalendarOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setCalendarOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [calendarOpen]);
    const openCalendar = () => {
        const parts = parseDateParts(dateValue);
        setCalendarYear(parts.year);
        setCalendarMonth(parts.month);
        setCalendarOpen(open => !open);
    };
    const onFormSubmit = (event) => {
        event.preventDefault();
        const trimmed = title.trim();
        if (!trimmed || !dateValue || !timeValue) {
            setLocalError(M.SCHEDULE_REQUIRED);
            logger.warn(`[门户] ${new Date().toISOString()} 预定会议校验失败 | 缺少主题或开始时间`);
            return;
        }
        setLocalError('');
        const scheduledStart = toScheduledIso(dateValue, timeValue);
        const scheduledEnd = addMinutesIso(scheduledStart, durationMinutes);
        const startAudioMuted = toStartAudioMuted(muteMode);
        logger.info(`[门户] ${new Date().toISOString()} 提交预定会议`
            + ` | title=${trimmed} start=${scheduledStart} durationMin=${durationMinutes}`
            + ` mute=${muteMode} autoRecord=${autoCloudRecording}`);
        onSubmit({
            autoCloudRecording,
            scheduledEnd,
            scheduledStart,
            startAudioMuted,
            title: trimmed
        });
    };
    return (React.createElement("div", { className: 'portal-modal-backdrop', onClick: onClose, role: 'presentation' },
        React.createElement("form", { className: 'portal-modal portal-schedule-modal', onClick: e => e.stopPropagation(), onSubmit: onFormSubmit },
            React.createElement("div", { className: 'portal-modal-header' },
                React.createElement("h3", null, M.SCHEDULE_MEETING),
                React.createElement("button", { className: 'portal-modal-close', onClick: onClose, title: M.CLOSE, type: 'button' },
                    React.createElement("svg", { fill: 'none', height: '20', viewBox: '0 0 24 24', width: '20' },
                        React.createElement("path", { d: 'M18 6 6 18M6 6l12 12', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })))),
            React.createElement("div", { className: 'portal-schedule-row' },
                React.createElement("span", { className: 'portal-schedule-label' }, M.SCHEDULE_TOPIC),
                React.createElement("div", { className: 'portal-schedule-title-wrap' },
                    React.createElement("input", { onChange: e => setTitle(e.target.value), value: title }),
                    title ? (React.createElement("button", { className: 'portal-schedule-clear', onClick: () => setTitle(''), title: M.SCHEDULE_CLEAR_TITLE, type: 'button' }, "\u00D7")) : null)),
            React.createElement("div", { className: 'portal-schedule-row' },
                React.createElement("span", { className: 'portal-schedule-label' }, M.SCHEDULE_START_SHORT),
                React.createElement("div", { className: 'portal-schedule-start' },
                    React.createElement("div", { className: 'portal-schedule-date-wrap', ref: dateWrapRef },
                        React.createElement("button", { className: 'portal-schedule-date', onClick: openCalendar, type: 'button' },
                            React.createElement("span", null, formatDateWithWeekday(dateValue))),
                        calendarOpen ? (React.createElement("div", { className: 'portal-schedule-calendar' },
                            React.createElement("div", { className: 'portal-schedule-calendar-nav' },
                                React.createElement("button", { onClick: () => {
                                        const next = addCalendarMonths(calendarYear, calendarMonth, -1);
                                        setCalendarYear(next.year);
                                        setCalendarMonth(next.month);
                                    }, type: 'button' }, "\u2039"),
                                React.createElement("span", null, formatCalendarTitle(calendarYear, calendarMonth)),
                                React.createElement("button", { onClick: () => {
                                        const next = addCalendarMonths(calendarYear, calendarMonth, 1);
                                        setCalendarYear(next.year);
                                        setCalendarMonth(next.month);
                                    }, type: 'button' }, "\u203A")),
                            React.createElement("div", { className: 'portal-schedule-calendar-week' }, CALENDAR_WEEKDAYS.map(label => (React.createElement("span", { key: label }, label)))),
                            React.createElement("div", { className: 'portal-schedule-calendar-grid' }, calendarCells.map((cell, index) => (React.createElement("button", { className: [
                                    cell.inMonth ? '' : 'is-outside',
                                    cell.dateValue === dateValue ? 'is-selected' : ''
                                ].filter(Boolean).join(' '), key: `${cell.dateValue}-${index}`, onClick: () => {
                                    setDateValue(cell.dateValue);
                                    setCalendarOpen(false);
                                }, type: 'button' }, cell.day)))))) : null),
                    React.createElement("select", { onChange: e => setTimeValue(e.target.value), value: timeValue }, timeOptions.map(option => (React.createElement("option", { key: option, value: option }, option)))))),
            React.createElement("div", { className: 'portal-schedule-row' },
                React.createElement("span", { className: 'portal-schedule-label' }, M.SCHEDULE_DURATION),
                React.createElement("select", { onChange: e => setDurationMinutes(Number(e.target.value)), value: durationMinutes }, SCHEDULE_DURATION_MINUTES.map(minutes => (React.createElement("option", { key: minutes, value: minutes }, formatDurationLabel(minutes)))))),
            React.createElement("div", { className: 'portal-schedule-row portal-schedule-row-top' },
                React.createElement("span", { className: 'portal-schedule-label' }, M.SCHEDULE_MUTE),
                React.createElement("div", { className: 'portal-schedule-radios' },
                    React.createElement("label", null,
                        React.createElement("input", { checked: muteMode === SCHEDULE_MUTE.ON, name: 'schedule-mute', onChange: () => setMuteMode(SCHEDULE_MUTE.ON), type: 'radio' }),
                        M.SCHEDULE_MUTE_ON),
                    React.createElement("label", null,
                        React.createElement("input", { checked: muteMode === SCHEDULE_MUTE.OFF, name: 'schedule-mute', onChange: () => setMuteMode(SCHEDULE_MUTE.OFF), type: 'radio' }),
                        M.SCHEDULE_MUTE_OFF),
                    React.createElement("label", null,
                        React.createElement("input", { checked: muteMode === SCHEDULE_MUTE.AFTER_6, name: 'schedule-mute', onChange: () => setMuteMode(SCHEDULE_MUTE.AFTER_6), type: 'radio' }),
                        M.SCHEDULE_MUTE_AFTER_6))),
            React.createElement("div", { className: 'portal-schedule-row' },
                React.createElement("span", { className: 'portal-schedule-label' }, M.SCHEDULE_RECORDING),
                React.createElement("label", { className: 'portal-schedule-check' },
                    React.createElement("input", { checked: autoCloudRecording, onChange: e => setAutoCloudRecording(e.target.checked), type: 'checkbox' }),
                    M.SCHEDULE_AUTO_RECORD)),
            localError || error ? (React.createElement("div", { className: 'portal-error inline' }, localError || error)) : null,
            React.createElement("button", { className: 'portal-schedule-submit', disabled: busy, type: 'submit' }, M.CONFIRM_SCHEDULE))));
}
